import { useState, type FormEvent } from "react";

export type CheckoutFormValues = {
  email: string;
  firstName: string;
  lastName: string;
  country: string;
  address: string;
  city: string;
  state: string;
  zip: string;
};

type Props = {
  onSubmit: (values: CheckoutFormValues) => Promise<void> | void;
  submitting: boolean;
};

const initial: CheckoutFormValues = {
  email: "",
  firstName: "",
  lastName: "",
  country: "United States",
  address: "",
  city: "",
  state: "",
  zip: "",
};

export default function CheckoutForm({ onSubmit, submitting }: Props) {
  const [values, setValues] = useState<CheckoutFormValues>(initial);
  const [errors, setErrors] = useState<Partial<Record<keyof CheckoutFormValues, string>>>({});

  const setField = <K extends keyof CheckoutFormValues>(key: K, v: CheckoutFormValues[K]) => {
    setValues((s) => ({ ...s, [key]: v }));
    if (errors[key]) setErrors((e) => ({ ...e, [key]: undefined }));
  };

  const validate = (): boolean => {
    const next: Partial<Record<keyof CheckoutFormValues, string>> = {};
    if (!values.email.trim()) next.email = "Required";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(values.email))
      next.email = "Invalid email";
    if (!values.firstName.trim()) next.firstName = "Required";
    if (!values.lastName.trim()) next.lastName = "Required";
    if (!values.address.trim()) next.address = "Required";
    if (!values.city.trim()) next.city = "Required";
    if (!values.state.trim()) next.state = "Required";
    if (!values.zip.trim()) next.zip = "Required";
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (submitting) return;
    if (!validate()) return;
    onSubmit(values);
  };

  const input =
    "w-full h-11 px-3 rounded-md bg-background-50 border border-background-300 focus:border-foreground-700 focus:outline-none text-sm text-foreground-950 placeholder:text-foreground-500";
  const labelCls = "text-xs text-foreground-700 mb-1.5 block";
  const errCls = "text-xs text-primary-700 mt-1";

  return (
    <form onSubmit={handleSubmit} noValidate className="space-y-8" id="checkout-place-order">
      {/* Contact */}
      <section>
        <h2 className="text-sm font-medium text-foreground-950 mb-4">Contact</h2>
        <div>
          <label className={labelCls} htmlFor="co-email">Email</label>
          <input
            id="co-email"
            name="email"
            type="email"
            value={values.email}
            onChange={(e) => setField("email", e.target.value)}
            className={input}
            autoComplete="email"
          />
          {errors.email && <p className={errCls}>{errors.email}</p>}
        </div>
      </section>

      {/* Shipping */}
      <section>
        <h2 className="text-sm font-medium text-foreground-950 mb-4">Shipping address</h2>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className={labelCls} htmlFor="co-firstName">First name</label>
            <input
              id="co-firstName"
              name="first_name"
              value={values.firstName}
              onChange={(e) => setField("firstName", e.target.value)}
              className={input}
              autoComplete="given-name"
            />
            {errors.firstName && <p className={errCls}>{errors.firstName}</p>}
          </div>
          <div>
            <label className={labelCls} htmlFor="co-lastName">Last name</label>
            <input
              id="co-lastName"
              name="last_name"
              value={values.lastName}
              onChange={(e) => setField("lastName", e.target.value)}
              className={input}
              autoComplete="family-name"
            />
            {errors.lastName && <p className={errCls}>{errors.lastName}</p>}
          </div>
        </div>

        <div className="mt-4">
          <label className={labelCls} htmlFor="co-address">Address</label>
          <input
            id="co-address"
            name="street"
            value={values.address}
            onChange={(e) => setField("address", e.target.value)}
            className={input}
            autoComplete="street-address"
          />
          {errors.address && <p className={errCls}>{errors.address}</p>}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-4">
          <div>
            <label className={labelCls} htmlFor="co-city">City</label>
            <input
              id="co-city"
              name="city"
              value={values.city}
              onChange={(e) => setField("city", e.target.value)}
              className={input}
              autoComplete="address-level2"
            />
            {errors.city && <p className={errCls}>{errors.city}</p>}
          </div>
          <div>
            <label className={labelCls} htmlFor="co-state">State</label>
            <input
              id="co-state"
              name="state"
              value={values.state}
              onChange={(e) => setField("state", e.target.value)}
              className={input}
              autoComplete="address-level1"
            />
            {errors.state && <p className={errCls}>{errors.state}</p>}
          </div>
          <div>
            <label className={labelCls} htmlFor="co-zip">ZIP</label>
            <input
              id="co-zip"
              name="zip"
              value={values.zip}
              onChange={(e) => setField("zip", e.target.value)}
              className={input}
              autoComplete="postal-code"
            />
            {errors.zip && <p className={errCls}>{errors.zip}</p>}
          </div>
        </div>

        <div className="mt-4">
          <label className={labelCls} htmlFor="co-country">Country</label>
          <select
            id="co-country"
            name="country"
            value={values.country}
            onChange={(e) => setField("country", e.target.value)}
            className={`${input} pr-3`}
          >
            <option>United States</option>
            <option>Canada</option>
            <option>United Kingdom</option>
            <option>Australia</option>
          </select>
        </div>
      </section>

      {/* Honeypot */}
      <input
        type="text"
        name="company_alt"
        value=""
        onChange={() => {}}
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        className="checkout-honeypot-field"
      />

      <button
        type="submit"
        disabled={submitting}
        className="w-full inline-flex items-center justify-center gap-2 h-12 rounded-full bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-medium transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed"
      >
        {submitting ? (
          <>
            <i className="ri-loader-4-line animate-spin"></i>
            Placing order...
          </>
        ) : (
          "Place order"
        )}
      </button>
    </form>
  );
}